import { Select, SelectItem, SelectSkeleton } from "@carbon/react";
import { gql, useQuery } from '@apollo/client';
import { useEffect } from "react";

const GET_ALL_GROUPS = gql`
    query GetAllGroups {
        allGroups {
            __typename
            id
            name
            ... on SchoolClass {
                senior
                teacher {
                    id
                    surname
                    lastname
                }
            }
        }
    }
`;

export default function ClassSelector({ selectedClass = null, onClassChange, enableAllClasses = false }) {
    const { loading, error, data } = useQuery(GET_ALL_GROUPS, {
        fetchPolicy: 'cache-and-network', // Use cache for better performance
        errorPolicy: 'all'
    });

    useEffect(() => {
        if (!loading && data && data.allGroups.length > 0 && !selectedClass && !enableAllClasses) {
            // Only auto-select first group if no selectedClass is provided
            // use next tick to avoid racing with the Select internal events
            setTimeout(() => onClassChange && onClassChange(data.allGroups[0].id), 0);
        }
    }, [loading, data, selectedClass, enableAllClasses, onClassChange]);

    if (loading) return <SelectSkeleton />;
    if (error) return <p>Error loading groups: {error.message}</p>;

    const handleOnChange = (event) => {
        if (onClassChange) {
            onClassChange(event.target.value);
        }
    };

    return (
        <Select
            id={`select-1`}
            className="w-full"
            labelText="Klasse auswählen"
            onChange={handleOnChange}
            // Only control the Select when selectedClass is provided.
            // Otherwise rely on defaultValue to avoid immediately overriding a user selection.
            value={selectedClass ?? (enableAllClasses ? "*" : undefined)}
            defaultValue={!selectedClass && !enableAllClasses && data && data.allGroups.length > 0 ? data.allGroups[0].id : undefined}
        >
            {enableAllClasses && <SelectItem value="*" text="Alle Klassen" />}
            {data.allGroups.map((grp) => {
                if (grp.__typename === 'SchoolClass') {
                    const teacherName = grp.teacher ? `${grp.teacher.lastname.toUpperCase()} ${grp.teacher.surname}` : 'unbekannt';
                    return (
                        <SelectItem
                            key={grp.id}
                            value={grp.id}
                            text={`${grp.name} (${teacherName}) - ${grp.senior ? 'Oberstufe' : 'Unterstufe'}`}
                        />
                    );
                }

                // Generic Group representation: just show the name
                return (
                    <SelectItem
                        key={grp.id}
                        value={grp.id}
                        text={`${grp.name} (Gruppe)`}
                    />
                );
            })}
        </Select>
    );
}